import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useLangMap } from '../../hooks/useLanguages';
import { useAppConfig } from '../../hooks/useAppConfig';
import { fetchData } from '../../services/dataApi';
import { SpeakCard } from '../practice/PracticePage';
import { ChunkyButton } from '../../components/GameUI';

const PACK_IDS = [1, 2, 3, 4];

function mergePack(targetPack, nativePack) {
  const nativeBySource = new Map((nativePack?.items ?? []).map((item) => [item.sourceKey || item.id, item]));
  return {
    ...targetPack,
    cards: (targetPack.items ?? []).map((item) => ({
      key: item.id,
      targetText: item.text,
      nativeText: nativeBySource.get(item.sourceKey || item.id)?.text || item.meaning,
      difficulty: item.difficulty,
      type: item.type,
    })),
  };
}

async function loadNativePack(nativeCode, id) {
  const english = await fetchData(`speaking-listening/en/${id}.json`);
  if (nativeCode === 'en') return english;
  const localized = await fetchData(`speaking-listening/${nativeCode}/${id}.json`)
    .catch(() => null);
  if (!localized) return english;

  const localizedBySource = new Map(
    (localized.items ?? []).map((item) => [item.sourceKey || item.id, item]),
  );
  return {
    ...english,
    ...localized,
    items: (english.items ?? []).map((item) => {
      const overlay = localizedBySource.get(item.sourceKey || item.id);
      return { ...item, ...overlay, text: overlay?.text || item.text };
    }),
  };
}

export default function SpeakPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { languagePrefs } = useAuth();
  const { langMap } = useLangMap();
  const [packs, setPacks] = useState([]);
  const [activeId, setActiveId] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [completed, setCompleted] = useState(false);

  const nativeCode = languagePrefs?.nativeLanguage || 'en';
  const targetCode = languagePrefs?.targetLanguage || 'en';
  const targetLang = langMap[targetCode];
  const nativeLang = langMap[nativeCode];
  const config = useAppConfig(nativeCode);
  const speakStep = config.learningSteps.find((step) => step.code === 'speak');
  const typeLabel = (type) => {
    if (type === 'words' || type === 'word') return t('vocab.title');
    if (type === 'sentences' || type === 'sentence') return t('practice.sentences');
    return t('practice.speak');
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    setCompleted(false);
    Promise.all(PACK_IDS.map(async (id) => {
      const [targetPack, nativePack] = await Promise.all([
        fetchData(`speaking-listening/${targetCode}/${id}.json`),
        loadNativePack(nativeCode, id),
      ]);
      return mergePack(targetPack, nativePack);
    }))
      .then((data) => {
        if (!active) return;
        setPacks(data);
        setActiveId(data[0]?.id || 1);
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setPacks([]);
        setError(true);
        setLoading(false);
      });
    return () => { active = false; };
  }, [nativeCode, targetCode]);

  const activePack = useMemo(() => packs.find((pack) => pack.id === activeId) || packs[0], [packs, activeId]);
  const activeIndex = packs.findIndex((pack) => pack.id === activePack?.id);

  function selectPack(id) {
    setActiveId(id);
    setCompleted(false);
  }

  function continueAfterPack() {
    if (activeIndex >= 0 && activeIndex < packs.length - 1) {
      selectPack(packs[activeIndex + 1].id);
    } else {
      setCompleted(true);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-slate-50 to-fuchsia-50 px-4 py-10">
      <div className="mx-auto max-w-2xl space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-black uppercase tracking-widest text-violet-600">{speakStep?.step ?? 10} · {speakStep?.label ?? 'Speak'}</p>
            <h1 className="mt-1 text-3xl font-black text-slate-900">{speakStep?.label ?? 'Speak'}</h1>
            <p className="mt-2 text-sm text-slate-500">
              {t('practice.sayThisIn', { lang: targetLang?.label || targetCode.toUpperCase() })}
            </p>
            {nativeLang && targetLang && (
              <p className="mt-2 text-xs font-bold text-slate-500">
                {nativeLang.label} → {targetLang.label}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="shrink-0 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-500 shadow-sm"
          >
            {t('common.back')}
          </button>
        </div>

        {loading && (
          <div className="space-y-3">
            {PACK_IDS.map((id) => <div key={id} className="h-24 animate-pulse rounded-3xl bg-slate-200" />)}
          </div>
        )}

        {!loading && error && (
          <div className="rounded-3xl border border-red-200 bg-white p-10 text-center">
            <p className="text-4xl">📭</p>
            <p className="mt-3 font-black text-slate-800">{t('learn.failed')}</p>
            <button type="button" onClick={() => window.location.reload()} className="mt-4 rounded-xl bg-violet-600 px-5 py-2.5 text-sm font-bold text-white">
              {t('common.retry')}
            </button>
          </div>
        )}

        {!loading && !error && activePack && (
          <>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {packs.map((pack) => (
                <button
                  type="button"
                  key={pack.id}
                  onClick={() => selectPack(pack.id)}
                  className={`rounded-2xl border px-2 py-3 text-center transition ${activeId === pack.id ? 'border-violet-500 bg-violet-600 text-white shadow-md' : 'border-slate-200 bg-white text-slate-600'}`}
                >
                  <span className="block text-xl">{pack.icon}</span>
                  <span className="mt-1 block text-xs font-black">{typeLabel(pack.type)}</span>
                </button>
              ))}
            </div>

            <div className="rounded-2xl border border-violet-100 bg-white px-5 py-4">
              <p className="font-black text-slate-900">{activePack.icon} {typeLabel(activePack.type)}</p>
              <p className="mt-1 text-sm text-slate-500">{t('practice.sayThisIn', { lang: targetLang?.label || targetCode.toUpperCase() })}</p>
            </div>

            {completed ? (
              <div className="rounded-3xl border border-emerald-200 bg-white p-10 text-center shadow-sm">
                <p className="text-6xl">🎙️</p>
                <h2 className="mt-4 text-2xl font-black text-slate-900">{t('exercise.testComplete')}</h2>
                <ChunkyButton color="#7C3AED" shadowColor="#6D28D9" onClick={() => selectPack(1)} className="mt-6 w-full py-3.5 text-white">
                  {t('practice.tryAgain')}
                </ChunkyButton>
              </div>
            ) : (
              <SpeakCard
                key={`${targetCode}-${activePack.id}`}
                cards={activePack.cards}
                targetLang={targetLang || { code: targetCode, label: targetCode.toUpperCase() }}
                category={activePack.type}
                onComplete={continueAfterPack}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
